import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
  ExternalLink,
  Copy,
  Check,
  BookOpen,
  Code,
  FileText,
  Link as LinkIcon,
  Target,
  Wrench,
} from 'lucide-react';
import { useScanResults } from '../../hooks/useScanResults';
import { getFixGuidance, type CodeExample } from '../../data/fixGuidance';
import { truncateUrl } from '../../utils/formatting';
import type { AffectedNode } from '../../types';

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-light/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <span className="text-coral">{icon}</span>
          <span className="text-heading-3 font-semibold">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown size={20} className="text-gray-medium" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium" />
        )}
      </button>
      {isOpen && (
        <div className="border-t border-gray-light p-4">
          {children}
        </div>
      )}
    </div>
  );
}

interface CodeBlockProps {
  code: string;
  language?: 'html' | 'css' | 'javascript';
  title?: string;
}

function CodeBlock({ code, language = 'html', title }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighting classes based on language
  const getLanguageClass = () => {
    switch (language) {
      case 'html':
        return 'text-blue-400';
      case 'css':
        return 'text-purple-400';
      case 'javascript':
        return 'text-yellow-400';
      default:
        return 'text-gray-300';
    }
  };

  return (
    <div className="rounded-lg overflow-hidden bg-gray-900">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
          <span className="text-body-sm text-gray-400">{title}</span>
          <span className={`text-body-sm ${getLanguageClass()}`}>{language}</span>
        </div>
      )}
      <div className="relative">
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 rounded hover:bg-gray-700 transition-colors"
          title="Copy code"
        >
          {copied ? (
            <Check size={16} className="text-status-success" />
          ) : (
            <Copy size={16} className="text-gray-400" />
          )}
        </button>
        <pre className="p-4 overflow-x-auto text-body-sm">
          <code className={`${getLanguageClass()} whitespace-pre-wrap break-words`}>
            {code}
          </code>
        </pre>
      </div>
    </div>
  );
}

interface SeverityBadgeProps {
  severity: 'critical' | 'serious' | 'moderate' | 'minor';
}

function SeverityBadge({ severity }: SeverityBadgeProps) {
  const config = {
    critical: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: <AlertTriangle size={14} />,
    },
    serious: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-200',
      icon: <AlertCircle size={14} />,
    },
    moderate: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      icon: <Info size={14} />,
    },
    minor: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      icon: <Info size={14} />,
    },
  };

  const { bg, text, border, icon } = config[severity];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-sm font-medium ${bg} ${text} border ${border}`}
    >
      {icon}
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
}

interface ToolBadgeProps {
  tool: string;
}

function ToolBadge({ tool }: ToolBadgeProps) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-sm font-medium bg-gray-100 text-gray-700 border border-gray-200">
      <Code size={14} />
      {tool}
    </span>
  );
}

function CodeExampleCard({ example }: { example: CodeExample }) {
  return (
    <div className="space-y-3">
      <h4 className="text-body font-semibold">{example.title}</h4>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-body-sm text-status-error font-medium mb-2 flex items-center gap-1">
            <AlertTriangle size={14} />
            Before (Incorrect)
          </div>
          <CodeBlock code={example.before} language={example.language} />
        </div>
        <div>
          <div className="text-body-sm text-status-success font-medium mb-2 flex items-center gap-1">
            <Check size={14} />
            After (Correct)
          </div>
          <CodeBlock code={example.after} language={example.language} />
        </div>
      </div>
    </div>
  );
}

function AffectedElementCard({ node, index }: { node: AffectedNode; index: number }) {
  return (
    <div className="border border-gray-light rounded-lg overflow-hidden">
      <div className="bg-gray-light/50 px-4 py-2 border-b border-gray-light">
        <span className="text-body-sm font-medium text-gray-medium">
          Element {index + 1}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-body-sm text-gray-medium mb-1">HTML</div>
          <CodeBlock code={node.html} language="html" />
        </div>
        {node.target && node.target.length > 0 && (
          <div>
            <div className="text-body-sm text-gray-medium mb-1">CSS Selector</div>
            <code className="block bg-gray-100 px-3 py-2 rounded text-body-sm font-mono text-gray-700 break-all">
              {node.target.join(' > ')}
            </code>
          </div>
        )}
        {node.failureSummary && (
          <div>
            <div className="text-body-sm text-gray-medium mb-1">Failure Summary</div>
            <p className="text-body-sm text-gray-700 bg-red-50 border border-red-100 rounded px-3 py-2">
              {node.failureSummary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function IssueDetailPage() {
  const { siteId, scanId, pageUrl: encodedPageUrl, issueId } = useParams<{
    siteId: string;
    scanId: string;
    pageUrl: string;
    issueId: string;
  }>();

  const pageUrl = encodedPageUrl ? decodeURIComponent(encodedPageUrl) : '';
  const { scan, pageResults, isLoading, error } = useScanResults(siteId!, scanId!);

  // Find the specific page result and issue
  const pageResult = useMemo(() => {
    return pageResults.find((p) => p.pageUrl === pageUrl);
  }, [pageResults, pageUrl]);

  const issue = useMemo(() => {
    if (!pageResult?.accessibilityIssues) return null;
    return pageResult.accessibilityIssues.find((i) => i.ruleId === issueId);
  }, [pageResult, issueId]);

  // Get fix guidance from our data file
  const fixGuidance = useMemo(() => {
    if (!issueId) return null;
    return getFixGuidance(issueId);
  }, [issueId]);

  // Determine tool source (mock for now - would come from scan config)
  const toolSource = 'axe-core';

  if (isLoading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !scan || !pageResult) {
    return (
      <div className="py-8">
        <div className="container-content">
          <div className="card text-center py-12">
            <AlertTriangle size={48} className="mx-auto text-status-warning mb-4" />
            <h2 className="text-heading-3 font-semibold mb-2">Issue Not Found</h2>
            <p className="text-body text-gray-medium mb-6">
              {error || 'The issue you are looking for could not be found.'}
            </p>
            <Link to={`/sites/${siteId}/scans/${scanId}`} className="btn-primary">
              Back to Scan Results
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Use fix guidance data or fall back to issue data
  const title = fixGuidance?.title || issue?.description || `Issue: ${issueId}`;
  const description = fixGuidance?.description || issue?.description || '';
  const impact = fixGuidance?.impact || issue?.impact || 'moderate';
  const wcagCriteria = fixGuidance?.wcagCriteria ||
    (issue?.wcagCriteria?.map((criterion) => ({
      id: criterion,
      name: criterion,
      level: (issue.wcagLevel as 'A' | 'AA' | 'AAA') || 'A',
      url: `https://www.w3.org/WAI/WCAG21/Understanding/${criterion.toLowerCase().replace('.', '-')}.html`,
    })) || []);

  return (
    <div className="py-8">
      <div className="container-content">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center gap-2 text-body-sm text-gray-medium mb-6 flex-wrap">
          <Link
            to={`/sites/${siteId}/scans/${scanId}`}
            className="hover:text-coral transition-colors"
          >
            Scan Results
          </Link>
          <ChevronRight size={14} />
          <span className="truncate max-w-[200px]" title={pageUrl}>
            {truncateUrl(pageUrl, 40)}
          </span>
          <ChevronRight size={14} />
          <span className="text-dark-text font-medium truncate max-w-[200px]">
            {issueId}
          </span>
        </nav>

        {/* Back Link */}
        <Link
          to={`/sites/${siteId}/scans/${scanId}`}
          className="inline-flex items-center gap-2 text-body-sm text-gray-medium hover:text-coral mb-6"
        >
          <ArrowLeft size={16} />
          Back to Results
        </Link>

        {/* Header Section */}
        <div className="section-dark rounded-card p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-heading-1 font-bold text-white mb-4">
                {title}
              </h1>
              <p className="text-body text-white/70 mb-4">
                {description}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <SeverityBadge severity={impact as 'critical' | 'serious' | 'moderate' | 'minor'} />
                <ToolBadge tool={toolSource} />
                {issue?.isWcag22 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                    WCAG 2.2
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-body-sm text-white/50 mb-1">Affected Elements</div>
              <div className="text-display font-bold text-coral">
                {issue?.nodes?.length || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-4">
          {/* Description Section */}
          {fixGuidance?.whyItMatters && (
            <CollapsibleSection
              title="Why It Matters"
              icon={<Info size={20} />}
              defaultOpen={true}
            >
              <div className="prose prose-sm max-w-none">
                <p className="text-body text-gray-700 whitespace-pre-line">
                  {fixGuidance.whyItMatters}
                </p>
              </div>
            </CollapsibleSection>
          )}

          {/* WCAG Criteria Section */}
          {wcagCriteria.length > 0 && (
            <CollapsibleSection
              title="WCAG Success Criteria"
              icon={<BookOpen size={20} />}
              defaultOpen={true}
            >
              <ul className="space-y-3">
                {wcagCriteria.map((criterion) => (
                  <li
                    key={criterion.id}
                    className="flex items-center justify-between p-3 bg-gray-light/50 rounded-lg"
                  >
                    <div>
                      <span className="font-semibold text-body">
                        {criterion.id} - {criterion.name}
                      </span>
                      <span className="ml-2 px-2 py-0.5 text-body-sm rounded bg-coral/10 text-coral font-medium">
                        Level {criterion.level}
                      </span>
                    </div>
                    <a
                      href={criterion.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-coral hover:text-coral-dark transition-colors text-body-sm"
                    >
                      View docs
                      <ExternalLink size={14} />
                    </a>
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {/* Fix Guidance Section */}
          {fixGuidance?.howToFix && (
            <CollapsibleSection
              title="How to Fix"
              icon={<Wrench size={20} />}
              defaultOpen={true}
            >
              <div className="prose prose-sm max-w-none">
                <div className="text-body text-gray-700 whitespace-pre-line">
                  {fixGuidance.howToFix.split('\n').map((line, i) => {
                    // Simple markdown-like rendering
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return (
                        <p key={i} className="font-semibold my-2">
                          {line.replace(/\*\*/g, '')}
                        </p>
                      );
                    }
                    if (line.startsWith('- ')) {
                      return (
                        <li key={i} className="ml-4 list-disc">
                          {line.substring(2)}
                        </li>
                      );
                    }
                    if (line.match(/^\d+\./)) {
                      return (
                        <li key={i} className="ml-4 list-decimal">
                          {line.substring(line.indexOf('.') + 2)}
                        </li>
                      );
                    }
                    if (line.trim() === '') {
                      return <br key={i} />;
                    }
                    return <p key={i}>{line}</p>;
                  })}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Code Examples Section */}
          {fixGuidance?.codeExamples && fixGuidance.codeExamples.length > 0 && (
            <CollapsibleSection
              title="Code Examples"
              icon={<Code size={20} />}
              defaultOpen={false}
            >
              <div className="space-y-6">
                {fixGuidance.codeExamples.map((example, index) => (
                  <CodeExampleCard key={index} example={example} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Affected Elements Section */}
          {issue?.nodes && issue.nodes.length > 0 && (
            <CollapsibleSection
              title={`Affected Elements (${issue.nodes.length})`}
              icon={<Target size={20} />}
              defaultOpen={false}
            >
              <div className="space-y-4">
                {issue.nodes.map((node, index) => (
                  <AffectedElementCard key={index} node={node} index={index} />
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* External Resources Section */}
          {fixGuidance?.externalResources && fixGuidance.externalResources.length > 0 && (
            <CollapsibleSection
              title="External Resources"
              icon={<LinkIcon size={20} />}
              defaultOpen={false}
            >
              <div className="grid sm:grid-cols-2 gap-3">
                {fixGuidance.externalResources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 p-4 bg-gray-light/50 rounded-lg hover:bg-gray-light transition-colors group"
                  >
                    <FileText
                      size={20}
                      className="text-gray-medium group-hover:text-coral transition-colors flex-shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-body font-medium text-dark-text group-hover:text-coral transition-colors">
                        {resource.title}
                      </div>
                      <div className="text-body-sm text-gray-medium">
                        {resource.source}
                      </div>
                    </div>
                    <ExternalLink
                      size={16}
                      className="text-gray-medium group-hover:text-coral transition-colors flex-shrink-0 mt-0.5"
                    />
                  </a>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Help URL from Issue (fallback if no fix guidance) */}
          {!fixGuidance && issue?.helpUrl && (
            <CollapsibleSection
              title="Learn More"
              icon={<LinkIcon size={20} />}
              defaultOpen={true}
            >
              <a
                href={issue.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-coral hover:text-coral-dark transition-colors"
              >
                View documentation for this rule
                <ExternalLink size={16} />
              </a>
            </CollapsibleSection>
          )}
        </div>
      </div>
    </div>
  );
}
